import { useState, useRef, useCallback, useEffect } from 'react';
import { useSampleUpdate } from '@/features/samples/hooks/useSampleUpdate';
import { toast } from 'sonner';

export type DraftValues = Record<string, unknown>;

interface StoredDraft {
  values: DraftValues;
  timestamp: string;
}

interface AutoSaveState {
  isSaving: boolean;
  lastSaved: Date | null;
  hasUnsavedChanges: boolean;
}

interface UseAutoSaveParams {
  sampleId: string | number;
  sampleUpdatedAt: string;
  delay?: number;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  onDraftRestored?: (values: DraftValues) => void;
}

const DRAFT_KEY_PREFIX = 'sample-draft-';
const RETRY_DELAYS_MS = [2000, 4000, 8000];

function getDraftKey(sampleId: string | number): string {
  return `${DRAFT_KEY_PREFIX}${sampleId}`;
}

function writeDraft(sampleId: string | number, values: DraftValues): void {
  try {
    const draft: StoredDraft = { values, timestamp: new Date().toISOString() };
    localStorage.setItem(getDraftKey(sampleId), JSON.stringify(draft));
  } catch {
    // localStorage may be unavailable (private mode, storage full). In-memory state still works.
  }
}

function clearDraft(sampleId: string | number): void {
  try {
    localStorage.removeItem(getDraftKey(sampleId));
  } catch {
    // Same rationale as writeDraft.
  }
}

function readDraft(sampleId: string | number): StoredDraft | null {
  try {
    const raw = localStorage.getItem(getDraftKey(sampleId));
    if (!raw) return null;
    return JSON.parse(raw) as StoredDraft;
  } catch {
    return null;
  }
}

async function saveWithRetry(
  saveFn: () => Promise<unknown>,
  attemptsLeft: number = RETRY_DELAYS_MS.length
): Promise<void> {
  try {
    await saveFn();
  } catch (error) {
    if (attemptsLeft <= 0) {
      throw error;
    }
    const delayMs = RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - attemptsLeft];
    await new Promise<void>(resolve => setTimeout(resolve, delayMs));
    await saveWithRetry(saveFn, attemptsLeft - 1);
  }
}

export const useAutoSave = ({
  sampleId,
  sampleUpdatedAt,
  delay = 2000,
  onSuccess,
  onError,
  onDraftRestored,
}: UseAutoSaveParams) => {
  const [state, setState] = useState<AutoSaveState>({
    isSaving: false,
    lastSaved: null,
    hasUnsavedChanges: false,
  });

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pendingValuesRef = useRef<DraftValues | null>(null);

  const { updateSampleAsync } = useSampleUpdate();

  // On mount: restore a draft that is newer than the last confirmed server save.
  useEffect(() => {
    const draft = readDraft(sampleId);
    if (!draft) return;

    const draftTime = new Date(draft.timestamp).getTime();
    const serverTime = new Date(sampleUpdatedAt).getTime();

    if (draftTime > serverTime) {
      pendingValuesRef.current = draft.values;
      setState(prev => ({ ...prev, hasUnsavedChanges: true }));
      onDraftRestored?.(draft.values);
    } else {
      clearDraft(sampleId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const performSave = useCallback(async (): Promise<void> => {
    const values = pendingValuesRef.current;
    if (!values) return;

    setState(prev => ({ ...prev, isSaving: true }));

    try {
      await saveWithRetry(() =>
        updateSampleAsync({ sampleId: String(sampleId), params: values })
      );

      clearDraft(sampleId);
      pendingValuesRef.current = null;

      setState({
        isSaving: false,
        lastSaved: new Date(),
        hasUnsavedChanges: false,
      });

      onSuccess?.();
    } catch (error) {
      setState(prev => ({ ...prev, isSaving: false }));
      toast.error('Error al guardar automáticamente. Los cambios se mantienen localmente.');
      onError?.(error as Error);
    }
  }, [sampleId, updateSampleAsync, onSuccess, onError]);

  const scheduleSave = useCallback((values: DraftValues): void => {
    // Update the ref synchronously so the save closure always reads the latest
    // accumulated field values, regardless of how many scheduleSave calls fire
    // before the debounce timeout elapses.
    pendingValuesRef.current = values;
    writeDraft(sampleId, values);

    if (timeoutRef.current !== undefined) {
      clearTimeout(timeoutRef.current);
    }

    setState(prev => ({ ...prev, hasUnsavedChanges: true }));
    timeoutRef.current = setTimeout(() => void performSave(), delay);
  }, [sampleId, delay, performSave]);

  const forceSave = useCallback((values: DraftValues): void => {
    if (timeoutRef.current !== undefined) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    pendingValuesRef.current = values;
    writeDraft(sampleId, values);
    void performSave();
  }, [sampleId, performSave]);

  const cancelScheduledSave = useCallback((): void => {
    if (timeoutRef.current !== undefined) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  return {
    ...state,
    scheduleSave,
    forceSave,
    cancelScheduledSave,
  };
};
